<!--
  Override of `@apexl/icarus-core`'s generic `ComponentWrapper.vue`.

  `ComponentWrapper` is a plain, everywhere-used structural div — the login,
  forgot-password, reset-password pages (io-user's `userDisplay` module) AND
  the install wizard (`/install`) all render their top-level content through
  it, marked with a `page-no-layout` class (set server-side by
  `NoUIWrapper`/`blankPage` — see `apexl/io-display`'s
  `Render::setActive('noUI')`). That class alone isn't enough to safely hang
  new chrome off: `/install` carries it too, and `InstallWizard.vue` already
  renders its own full-bleed wordmark + teal wrapper — adding a second one
  here would double up and break the wizard render this task must not touch.

  So the auth-family pre-auth chrome (teal full-bleed background + shared
  `Wordmark`) below is gated on BOTH the `page-no-layout` class AND the
  current route being one of the login/forgot-password/reset-password
  family. Every other `ComponentWrapper` usage — everywhere else in the
  app, plus `/install` — falls through to the untouched `v-else` branch,
  which is byte-for-byte the original core component's template/logic.
-->
<template>
  <div v-if="isAuthChrome" class="auth-page-chrome">
    <Wordmark />
    <div :class="classResolver(classes)">
      <DynamicComponents :components="components" />
    </div>
  </div>
  <div v-else :class="classResolver(classes)">
    <DynamicComponents :components="components" />
  </div>
</template>
<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import commonProps from '@apexl/icarus-core/src/components/_properties/common'
import DynamicComponents from "@apexl/icarus-core/src/components/partials/DynamicComponents.vue";
import classResolver from "@apexl/icarus-core/src/services/classResolver";
import Wordmark from '@root/components/elements/Wordmark.vue';

const props = defineProps({
  ...commonProps,
  components: {
    type: Array,
    default: () => ([]),
  },
});

const route = useRoute();

// Matches the five page-tree routes io-user's userDisplay module renders
// on `noUI` (see `design/surfaces/auth-screens.html`) — NOT `/install`,
// which shares the `page-no-layout` marker but owns its own chrome.
// `/reset-password-success` needed its own branch: `/^...reset-password)
// (\/|$)/` doesn't match it (the char after "reset-password" is `-`, not
// `/` or end-of-string) — confirmed visually in DDEV, the teal chrome was
// silently absent on that one route until this was split out.
const AUTH_CHROME_ROUTES = /^\/(login|forgot-password(\/.*)?|reset-password-success|reset-password\/.+)$/;

const isAuthChrome = computed(() => {
  const resolved = classResolver(props.classes);
  return resolved.split(' ').includes('page-no-layout')
    && AUTH_CHROME_ROUTES.test(route.path);
});
</script>

<style lang="scss">
.auth-page-chrome {
  position: fixed;
  inset: 0;
  z-index: 1000;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: var(--theme-bg-sidebar);
  padding: 48px 24px 40px;
  font-family: var(--theme-font-body);
}
</style>
